import React, {FC, ReactElement, useEffect, useMemo, useState} from 'react';
import {Grid, Table, TableProps} from "antd";
import {Model, PagedResponse} from "../../types";
import {ColumnsType} from "antd/lib/table";
import {DefaultFilters, useQuerySearchParams} from "../../config/queryClient";
import {useFilterPrefix} from "./Filters/FilterPrefixProvider";
import {SorterResult} from "antd/lib/table/interface";
import {QueryFilter} from "./Filters/types";
import {PAGE_SIZE, usePagination} from "./Pagination";
import { useOrdering } from './Ordering';

const {useBreakpoint} = Grid;

export interface DataTableProps<T extends Model> extends Omit<TableProps<T>, 'pagination'>{
    columns: ColumnsType<T>,
    query: string,
    defaultFilters?: DefaultFilters,
    autoExcludeRestrictedFields?: boolean,
    searchParamFilters?: (string | QueryFilter)[],
    pagination?: boolean,
    dataSourceTransform?: (data?: PagedResponse | Model[]) => T[]
}

const collectExpandableKeys = (rows: any[] = []): React.Key[] => {
    const keys: React.Key[] = [];

    rows.forEach((row) => {
        if (Array.isArray(row?.children) && row.children.length > 0) {
            keys.push(row.id);
            keys.push(...collectExpandableKeys(row.children));
        }
    });

    return keys;
};

export const DataTable: FC<DataTableProps<any>> = ({pagination: paginationEnabled = true, columns, query, defaultFilters, autoExcludeRestrictedFields, searchParamFilters = [], scroll, dataSourceTransform, expandable, ...rest}): ReactElement => {
    const filterPrefix = useFilterPrefix();

    const screens = useBreakpoint();
    const {page, setPage, pageFilters} = usePagination(paginationEnabled ? PAGE_SIZE : 0);
    const {setOrder, columnsWithOrder} = useOrdering(columns);

    const {data, isLoading} = useQuerySearchParams<PagedResponse | Model[]>(query, [...searchParamFilters, filterPrefix], {
        defaultFilters: {...defaultFilters, ...pageFilters},
        autoExcludeRestrictedFields
    });

    const tableData = useMemo(() => dataSourceTransform
        ? dataSourceTransform(data)
        : (paginationEnabled ? (data as PagedResponse)?.results : data as Model[]) || [], 
    [data, dataSourceTransform, paginationEnabled]);

    const autoExpandedKeys = useMemo(() => collectExpandableKeys(tableData as any[]), [tableData]);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    useEffect(() => {
        if (expandable?.defaultExpandAllRows) {
            setExpandedRowKeys(autoExpandedKeys);
        }
    }, [expandable?.defaultExpandAllRows, autoExpandedKeys]);

    const resolvedExpandable = expandable?.defaultExpandAllRows
        ? {
            ...expandable,
            expandedRowKeys,
            onExpandedRowsChange: (keys: readonly React.Key[]) => {
                setExpandedRowKeys([...keys]);
                expandable?.onExpandedRowsChange?.(keys);
            }
        }
        : expandable;

    return (
        <Table
            size='middle'
            {...rest}
            pagination={paginationEnabled ? {
                current: page,
                pageSize: PAGE_SIZE,
                total: (data as PagedResponse)?.count
            } : false}
            dataSource={tableData}
            loading={isLoading}
            columns={columnsWithOrder}
            scroll={{x: screens.xs ? 'max-content' : undefined, ...scroll}}
            rowKey='id'
            expandable={resolvedExpandable}
            // @ts-ignore
            onChange={(pagination, filters, sorter: SorterResult<Model>, {action}) => {
                if (action === 'paginate' && paginationEnabled) {
                    setPage(pagination.current);
                }

                if (action === 'sort' && sorter.field) {
                    setOrder(sorter.field, sorter.order)
                }
            }}
        />
    );
};
